/**
 * AbilityGroundsConfig — single visible toggle surface for /abilitygrounds.
 *
 * Sibling to BrawlConfig / PvEConfig / AbilityTestConfig. The Ability Grounds
 * is the CONSOLIDATED ability-testing home (the scattered /abilitytest range
 * map + AbilityTestArena dummies + the AbilityTestRig panel converge here). It
 * is a framed, deterministic arena with standing dummies, the data-driven
 * AbilityGroundsPanel (class/ability pickers + scenario bots + live render-truth
 * readout), and one-click quality capture.
 *
 * Wiring:
 *   - main.tsx               → reads on /abilitygrounds mount; injects URL params
 *                              + reset wipe (mirrors /brawl + /pve). Old
 *                              /abilitytest links are rewritten here to land in
 *                              the Grounds.
 *   - AbilityGroundsArena.ts → the map (id 'ability-grounds-arena').
 *
 * testhooks is on so the render-truth hook (__h2kRenderState / __h2kCast)
 * is live in the Grounds — the panel's live readout + the capture button read
 * it. (Before 2026-06-12 this comment CLAIMED testhooks was on but nothing
 * injected the param — the panel's cast buttons + readout were silently dead
 * on the bare route. `testhooks: true` below is what actually wires it.)
 */

export const ABILITY_GROUNDS_CONFIG = {
  // Cookie/cache hygiene — same as /brawl + /pve.
  resetOnLoad: true,

  // Class default — what DJ spawns into (picker still shows; this is only used
  // if a deep-link supplies ?class=).
  defaultClass: 'mage' as const,

  // HUD overrides — match /brawl + /pve.
  preset: 'live-lobby' as const,
  skipSplash: true,
  skipLogin: true,

  // Deep diagnostic windows stay available from the integrated card and the
  // 🛠 launcher, but do not auto-open over one another. An explicit
  // ?devPanelsOn=1 deep link still opts into the legacy four-window layout.

  // Render-truth hooks (__h2kRenderState / __h2kCast) — the panel's live
  // readout + class-follow depend on them.
  testhooks: true,

  // The quick class switcher is default-OFF everywhere WITH NO opt-ins
  // (DJ 2026-07-11 — old class roster, awaits overhaul). Cross-class
  // iteration on the Grounds: add ?classSwitcher=1 explicitly.

  // Auto-screenshot every successful cast to the server's ability-captures
  // store. Offsets derive from windup / live projectile flight / duration,
  // with event-triggered late beats through 4s and an explicit completion
  // receipt. 60-min retention; queryable via /api/diag/ability-captures.
  abilityCapture: true,

  // Auto-open the Ability Grounds control window (registry id 'abilityGrounds',
  // urlSeed 'grounds') — the mode's primary surface.
  seedWindows: ['grounds'],
} as const;
